import type { WithoutIndexSignature } from './WithoutIndexSignature';

import { parseStringValue } from '../parseStringValue';

export type RecursiveKeys<T> = T extends object
  ? {
      [K in keyof T]: NonNullable<T[K]> extends unknown[]
        ? `${K & string}`
        : NonNullable<T[K]> extends object
          ? | `${K & string}.${RecursiveKeys<
            WithoutIndexSignature<NonNullable<T[K]>>
          >}`
          | `${K & string}`
          : `${K & string}`;
    }[keyof T]
  : never;

type PathValue<T, P extends string> = P extends `${infer K}.${infer Rest}`
  ? K extends keyof T
    ? PathValue<NonNullable<T[K]>, Rest>
    : never
  : P extends keyof T
    ? T[P]
    : never;

// TODO challenge try to get typing working
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
export class TypedConfig<out TConfig> {
  constructor(
    readonly delegate: {
      get<T>(key?: string): T;
      getOptional<T>(key?: string): T | undefined;
    },
  ) {}

  get<Key extends RecursiveKeys<WithoutIndexSignature<TConfig>>>(
    key: Key,
  ): PathValue<TConfig, Key> {
    const value = this.delegate.get<PathValue<TConfig, Key>>(key);
    return parseStringValue(value);
  }

  getOptional<Key extends RecursiveKeys<WithoutIndexSignature<TConfig>>>(
    key: Key,
  ): PathValue<TConfig, Key> | undefined {
    const value = this.delegate.getOptional<PathValue<TConfig, Key>>(key);
    return value === undefined ? undefined : parseStringValue(value);
  }

  private convertValue<T>(value: unknown): T {
    if (value === undefined || value === null) {
      return value as T;
    }

    // Handle string to boolean conversion
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'true') return true as T;
      if (value.toLowerCase() === 'false') return false as T;

      // Handle string to number conversion
      const num = Number(value);
      if (!Number.isNaN(num) && value.trim() !== '') {
        return num as T;
      }
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return value.map(item => this.convertValue(item)) as T;
    }

    // Handle nested objects
    if (typeof value === 'object') {
      const converted: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(value)) {
        converted[key] = this.convertValue(val);
      }
      return converted as T;
    }

    return value as T;
  }
}
